import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import DateRange from '../../filters/DateRange';

const START_DATE_KEY = 'start_date';
const END_DATE_KEY = 'end_date';
const RANGE_KEYS = [START_DATE_KEY, END_DATE_KEY];

// Shown for every report, so the date fields and the generate button stay in
// place while the selector changes which report they apply to.
const DEFAULT_FIELDS = [
  { key: START_DATE_KEY, type: 'date', labelKey: 'administration.reporting.startDate' },
  { key: END_DATE_KEY, type: 'date', labelKey: 'administration.reporting.endDate' },
];

const ReportForm = ({ report, timeZone, isGenerating, onGenerate }) => {
  const { t } = useTranslation();
  const reportKey = _.get(report, 'key');

  // Values are tagged with the report they were entered for and reset during
  // render when the report changes. Clearing them in an effect would be too
  // late: the date picker, keyed on the report, remounts in the same render
  // and would copy the previous report's dates into its own state.
  const [form, setForm] = useState({ reportKey, values: {} });
  const isCurrent = form.reportKey === reportKey;
  if (!isCurrent) setForm({ reportKey, values: {} });
  const values = isCurrent ? form.values : {};

  const mergeValues = changes => setForm(current => ({ ...current, values: { ...current.values, ...changes } }));

  const setField = (key, value) => mergeValues({ [key]: value });

  const fields = _.get(report, 'param_fields.length') ? report.param_fields : DEFAULT_FIELDS;

  // The start/end pair is rendered as one range picker, matching the date
  // filters elsewhere in the dashboard; any other field keeps its own input.
  const startField = _.find(fields, { key: START_DATE_KEY });
  const endField = _.find(fields, { key: END_DATE_KEY });
  const hasRange = Boolean(startField && endField);
  const otherFields = hasRange ? _.reject(fields, field => _.includes(RANGE_KEYS, field.key)) : fields;

  const fieldLabel = field => field.label || t(field.labelKey);

  const setDates = (startDate, endDate) => mergeValues({ [START_DATE_KEY]: startDate, [END_DATE_KEY]: endDate });

  const missingRequired = fields
    .filter(field => field.required)
    .some(field => !values[field.key]);

  const generateLabel = _.get(report, 'output_format') === 'zip'
    ? t('administration.reporting.generateZip')
    : t('administration.reporting.generateCsv');

  return (
    <div className='u-margin-top--base'>
      <div className='row'>
        {hasRange && (
          <div className='column small-12 medium-6 u-margin-bottom--base'>
            <span className='u-txt--label'>{t('administration.reporting.dateRange')}</span>
            {/* Keyed on the report so the picker's own state resets with the values. */}
            <DateRange
              key={reportKey || 'none'}
              startDate={values[START_DATE_KEY]}
              endDate={values[END_DATE_KEY]}
              onDatesChange={setDates}
              startDatePlaceholderText={fieldLabel(startField)}
              endDatePlaceholderText={fieldLabel(endField)}
              disabled={!report}
              block={false}
              emitPartial
            />
            <small className='u-txt--label'>{t('administration.reporting.timeZoneNote', { timeZone })}</small>
          </div>
        )}
        {otherFields.map(field => (
          <div className='column small-12 medium-4' key={field.key}>
            <p>
              <span className='u-txt--label'>{fieldLabel(field)}</span><br />
              {field.type === 'select' ? (
                <select
                  value={values[field.key] || ''}
                  onChange={event => setField(field.key, event.target.value)}
                  disabled={!report}
                >
                  <option value=''>{t('administration.reporting.allOption')}</option>
                  {_.map(field.options, option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={values[field.key] || ''}
                  onChange={event => setField(field.key, event.target.value)}
                  disabled={!report}
                />
              )}
            </p>
          </div>
        ))}
      </div>

      <button
        type='button'
        className='o-sp-button o-sp-button--base o-sp-button--primary'
        disabled={!report || isGenerating || missingRequired}
        onClick={() => onGenerate(values)}
      >
        {isGenerating ? t('administration.reporting.generating') : generateLabel}
      </button>
    </div>
  );
};

ReportForm.propTypes = {
  report: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    output_format: PropTypes.string,
    param_fields: PropTypes.array,
  }),
  timeZone: PropTypes.string.isRequired,
  isGenerating: PropTypes.bool,
  onGenerate: PropTypes.func.isRequired,
};

export default ReportForm;
